import type { FormField } from '@/types/api/form'

import { isLayoutField } from './layoutField'

/** "Show this field when…" rules kept in options.showIf; mirrors the backend's FieldConditions. */
export const CONDITION_OPS = ['equals', 'notEquals', 'contains', 'isEmpty', 'isNotEmpty'] as const
export type ConditionOp = (typeof CONDITION_OPS)[number]

export const CONDITION_OP_LABELS: Record<ConditionOp, string> = {
  equals: 'is',
  notEquals: 'is not',
  contains: 'contains',
  isEmpty: 'is empty',
  isNotEmpty: 'is not empty',
}

export const MAX_CONDITION_RULES = 10

export interface ShowIfRule { fieldKey: string; op: ConditionOp; value?: string }
export interface ShowIf { match: 'all' | 'any'; rules: ShowIfRule[] }

type WithOptions = Pick<FormField, 'options'>

export function opNeedsValue(op: ConditionOp): boolean {
  return op !== 'isEmpty' && op !== 'isNotEmpty'
}

/** The field's rules, or null when it has none (always shown). */
export function showIfOf(field: WithOptions): ShowIf | null {
  const raw = (field.options as Record<string, unknown> | null)?.showIf as Partial<ShowIf> | undefined
  if (!raw || !Array.isArray(raw.rules) || raw.rules.length === 0) return null
  return { match: raw.match === 'any' ? 'any' : 'all', rules: raw.rules as ShowIfRule[] }
}

/** Answers by field key; blank answers count as missing. */
export function valuesByKey(
  fields: ReadonlyArray<Pick<FormField, 'id' | 'key'>>,
  valueOf: (field: Pick<FormField, 'id' | 'key'>) => string | null | undefined
): Record<string, string> {
  const byKey: Record<string, string> = {}
  for (const field of fields) {
    const value = valueOf(field)
    if (value && value.trim()) byKey[field.key] = value
  }
  return byKey
}

function contains(actual: string, expected: string): boolean {
  if (actual.startsWith('[')) {
    try {
      const items = JSON.parse(actual)
      if (Array.isArray(items)) return items.map(String).includes(expected)
    } catch {
      // not JSON: fall through to a text match
    }
  }
  return actual.toLowerCase().includes(expected.toLowerCase())
}

function ruleMet(rule: ShowIfRule, values: Record<string, string>): boolean {
  const actual = values[rule.fieldKey]
  const expected = rule.value ?? ''
  switch (rule.op) {
    case 'isEmpty': return actual === undefined
    case 'isNotEmpty': return actual !== undefined
    case 'equals': return actual !== undefined && actual === expected
    case 'notEquals': return actual === undefined || actual !== expected
    case 'contains': return actual !== undefined && contains(actual, expected)
    default: return false
  }
}

export function isFieldVisible(field: WithOptions, values: Record<string, string>): boolean {
  const showIf = showIfOf(field)
  if (!showIf) return true
  return showIf.match === 'any'
    ? showIf.rules.some((rule) => ruleMet(rule, values))
    : showIf.rules.every((rule) => ruleMet(rule, values))
}

/** Value fields before `field` in the form: the only ones its rules may look at. */
export function earlierValueFields<T extends Pick<FormField, 'id' | 'type' | 'orderIndex'>>(
  field: Pick<FormField, 'id'>, all: readonly T[]
): T[] {
  const sorted = [...all].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
  const at = sorted.findIndex((f) => f.id === field.id)
  return sorted.slice(0, at < 0 ? sorted.length : at).filter((f) => !isLayoutField(f) && f.type !== 'BUTTON')
}
